"use client"

import type React from "react"
import { useState } from "react"
import { ChevronDown } from "lucide-react"

interface FAQTab {
  id: number
  question: string
  answer: string
  icon: JSX.Element
}

const faqTabs: FAQTab[] = [
  {
    id: 1,
    question: "What is a Personal Health Record (PHR)?",
    answer:
      "A Personal Health Record (PHR) is a digital tool that allows you to store, organize, and manage your health information in one secure place. Unlike medical records maintained by healthcare providers, our PHR is controlled by you and enables you to share information with doctors, family members, and caregivers as needed.",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    id: 2,
    question: "How do I book an appointment?",
    answer:
      'Booking an appointment is simple. Navigate to the "Book Appointment" section, select your preferred doctor, choose an available date and time slot, and confirm your booking. You\'ll receive a confirmation on WhatsApp with all the details.',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    id: 3,
    question: "Is my health information secure?",
    answer:
      "Yes, we implement bank-level security measures including end-to-end encryption, multi-factor authentication, and strict access controls. Your data is encrypted both in transit and at rest, and you control who can access your information through permission settings. We comply with all healthcare data protection regulations.",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    id: 4,
    question: "Can I manage my family's health records?",
    answer:
      "Yes, our platform allows you to create and manage health profiles for family members, including children and elderly parents who may need assistance. You can keep track of appointments, medications, and health conditions for everyone in your family from a single account.",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  // {
  //   id: 5,
  //   question: "Is there a mobile app available?",
  //   answer:
  //     "Yes, we offer mobile apps for both iOS and Android devices. You can download them from the App Store or Google Play Store. The mobile app provides all the same features as the web version, with the added convenience of on-the-go access.",
  //   icon: (
  //     <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
  //       <path
  //         fillRule="evenodd"
  //         d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
  //         clipRule="evenodd"
  //       />
  //     </svg>
  //   ),
  // },
  {
    id: 6,
    question: "What should I do if I need technical support?",
    answer:
      "For technical support, you can contact our support team via email at support@swingbell.com. Our support team is available Monday to Friday, 9am to 6pm.",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
]

const FAQTabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(1)

  return (
    <section id="faqs" className="py-16 bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#008080] mb-4 font-nunito">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto font-roboto">
              Find answers to common questions about using Prescrable to manage your healthcare needs.
            </p>
          </div>

          {/* Tabs Navigation - Desktop */}
          <div className="hidden md:flex justify-center flex-wrap gap-3 mb-10">
            {faqTabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${activeTab === tab.id
                  ? "bg-gradient-to-r from-[#008080] to-[#00a3a3] text-white shadow-md scale-105"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:shadow-sm"
                  }`}
                onClick={() => setActiveTab(tab.id)}
                aria-selected={activeTab === tab.id}
                aria-controls={`faq-tab-${tab.id}`}
                role="tab"
              >
                <span
                  className={`${activeTab === tab.id ? "bg-white bg-opacity-20" : "bg-[#e6fffa]"} p-1.5 rounded-full`}
                >
                  {tab.icon}
                </span>
                <span className="text-sm font-roboto">
                  {tab.question.length > 25 ? `${tab.question.substring(0, 25)}...` : tab.question}
                </span>
              </button>
            ))}
          </div>

          {/* Accordion - Mobile & Desktop */}
          <div className="space-y-4">
            {faqTabs.map((tab) => (
              <div
                key={tab.id}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 ${activeTab === tab.id ? "ring-2 ring-[#008080]/20" : "hover:shadow-md"
                  }`}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left"
                  onClick={() => setActiveTab(activeTab === tab.id ? 0 : tab.id)}
                  aria-expanded={activeTab === tab.id}
                  aria-controls={`faq-content-${tab.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full ${activeTab === tab.id ? "bg-[#008080] text-white" : "bg-[#e6fffa] text-[#008080]"
                        } transition-colors duration-300`}
                    >
                      {tab.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800 font-nunito">{tab.question}</h3>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${activeTab === tab.id ? "transform rotate-180" : ""
                      }`}
                  />
                </button>

                <div
                  id={`faq-content-${tab.id}`}
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${activeTab === tab.id ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  role="region"
                  aria-labelledby={`faq-tab-${tab.id}`}
                >
                  <div className="p-5 pt-0 border-t border-gray-100">
                    <p className="text-gray-600 font-roboto leading-relaxed">{tab.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQTabs
